import { PackageClash, Holiday, EditorValidationError } from "../ISchema";
import i18n from "i18next";
import {
  INSTRUCTOR_CONTRACTED_BLOCKS_TYPENAME,
  mapInstructorContractedBlocksPayload,
} from "./instructorContractedBlocks";
import {
  SCHEDULED_BLOCKS_ASSIGNMENT_TYPENAME,
  getScheduledBlocksMessageKey,
  mapScheduledBlocksAssignmentPayload,
} from "./scheduledBlocksAssignment";
import {
  TOTAL_EXPECTED_BLOCKS_BY_LINK_TYPENAME,
  isTotalExpectedBlocksDeficit,
  mapTotalExpectedBlocksByLinkPayload,
} from "./totalExpectedBlocksByLink";

export type ValidationErrorType = EditorValidationError;

export interface IValidationError {
  incidence: ValidationErrorType | any;
}

export const errors = {
  ClassroomClash: i18n.t("assignation-edit.incidence-table.validations.classroom-clash.title"),
  BookingClassroomClash: i18n.t(
    "assignation-edit.incidence-table.validations.booking-classroom-clash.title",
  ),
  InstructorClash: i18n.t("assignation-edit.incidence-table.validations.instructor-clash.title"),
  LinkClash: i18n.t("assignation-edit.incidence-table.validations.link-clash.title"),
  SectionClash: i18n.t("assignation-edit.incidence-table.validations.section-clash.title"),
  PackageClash: i18n.t("assignation-edit.incidence-table.validations.package-clash.title"),
  InvalidInstructorTravel: i18n.t(
    "assignation-edit.incidence-table.validations.invalid-instructor-travel.title",
  ),
  InvalidPackageTravel: i18n.t(
    "assignation-edit.incidence-table.validations.invalid-package-travel.title",
  ),
  InvalidBuildingForCourse: i18n.t(
    "assignation-edit.incidence-table.validations.invalid-building-for-course.title",
  ),
  InvalidBlock: i18n.t("assignation-edit.incidence-table.validations.invalid-block.title"),
  InvalidBlockForInstructor: i18n.t(
    "assignation-edit.incidence-table.validations.invalid-block-for-instructor.title",
  ),
  ExceededAssignableBlocksUpcomingYear: i18n.t(
    "assignation-edit.incidence-table.validations.exceeded-assignable-blocks-upcoming-year.title",
  ),
  ExceededAssignableBlocksCurrentYear: i18n.t(
    "assignation-edit.incidence-table.validations.exceeded-assignable-blocks-current-year.title",
  ),
};

class SessionByType {
  private incidence: ValidationErrorType;
  constructor(error: IValidationError) {
    this.incidence = error.incidence;
  }

  private packageIncidenceMsg = (incidence: PackageClash) => {
    const complete =
      incidence.package.complete !== null
        ? incidence.package.complete
          ? `${i18n.t("assignation-edit.incidence-table.validations.package-clash.complete")}, `
          : `${i18n.t("assignation-edit.incidence-table.validations.package-clash.incomplete")}, `
        : "";
    const type = incidence.package.type !== null ? "*" : "";

    const countVacancies = incidence.package.vacancies ? incidence.package.vacancies : 0;
    const vacancies = countVacancies
      ? `${countVacancies} ${i18n.t(
          "assignation-edit.incidence-table.validations.package-clash.vacancies",
        )}`
      : "";

    const opt =
      incidence.package.hasPackageRules !== null
        ? incidence.package.hasPackageRules
          ? `${i18n.t("assignation-edit.incidence-table.validations.package-clash.required")}, `
          : `${i18n.t("assignation-edit.incidence-table.validations.package-clash.optional")}, `
        : "";
    const msgTitle = errors[incidence.__typename];
    return `${type}  ${msgTitle} ${incidence.package.code} (${type} ${complete} ${opt} ${vacancies}) `;
  };

  public getMessage() {
    switch (this.incidence.__typename) {
      case INSTRUCTOR_CONTRACTED_BLOCKS_TYPENAME: {
        const payload = mapInstructorContractedBlocksPayload(this.incidence);
        // instructor is optional on the type (defensive: keep working even if a
        // given error instance omits it), so the prefix stays conditional.
        const instructorPrefix = payload.instructorCode
          ? `${i18n.t(
              "assignation-edit.incidence-table.validations.instructor-contracted-blocks.instructor",
              { code: payload.instructorCode },
            )}: `
          : "";
        return `${instructorPrefix}${i18n.t(
          "assignation-edit.incidence-table.validations.instructor-contracted-blocks.description",
        )} ${i18n.t(
          "assignation-edit.incidence-table.validations.instructor-contracted-blocks.detail",
          { ...payload },
        )}`;
      }
      case SCHEDULED_BLOCKS_ASSIGNMENT_TYPENAME: {
        const payload = mapScheduledBlocksAssignmentPayload(this.incidence);
        return i18n.t(getScheduledBlocksMessageKey("detail", payload), { ...payload });
      }
      case TOTAL_EXPECTED_BLOCKS_BY_LINK_TYPENAME: {
        const payload = mapTotalExpectedBlocksByLinkPayload(this.incidence);
        // One typename carries both backend codes; `allowed` picks the wording.
        const description = isTotalExpectedBlocksDeficit(this.incidence)
          ? i18n.t(
              "assignation-edit.incidence-table.validations.total-expected-blocks.deficit-description",
            )
          : i18n.t(
              "assignation-edit.incidence-table.validations.total-expected-blocks.excess-description",
            );

        return `${description} ${i18n.t(
          "assignation-edit.incidence-table.validations.total-expected-blocks.detail",
          { ...payload },
        )}`;
      }
      case "PackageClash":
        return this.packageIncidenceMsg(this.incidence);
      case "ClassroomClash":
        return `${errors[this.incidence.__typename]} ${this.incidence?.classroom?.code}`;
      case "BookingClassroomClash":
        return `${errors[this.incidence.__typename]} ${this.incidence?.classroom?.code}`;
      case "InstructorClash":
        return `${errors[this.incidence.__typename]} ${this.incidence?.instructor?.code}`;
      case "InvalidBlockForInstructor":
        return i18n.t(
          "assignation-edit.incidence-table.validations.invalid-block-for-instructor.description",
          {
            code: this.incidence?.instructor?.code,
          },
        );
      case "StudentClash":
        const textCountStudent = `${i18n.t(
          "assignation-edit.incidence-table.validations.student-clash.student",
        )}${this.incidence.count > 1 ? "s" : ""}`;
        return `${i18n.t(
          "assignation-edit.incidence-table.validations.student-clash.description",
        )} - ${this.incidence?.count} ${textCountStudent}`;
      case "InstructorExceededWeeklyBlocks":
        return i18n.t(
          "assignation-edit.incidence-table.validations.instructor-exceeded-weekly-blocks.description",
          {
            instructorCode: this.incidence?.instructor?.code,
            assignableBlocks: this.incidence.assignableBlocks,
            exceededBlocks: this.incidence.exceededBlocks,
          },
        );
      case "InstructorAssignmentNotAllowed":
        return i18n.t(
          "assignation-edit.incidence-table.validations.instructor-assignment-not-allowed.description",
        );
      case "IntervalsAssignmentNotAllowed":
        return i18n.t(
          "assignation-edit.incidence-table.validations.intervals-assignment-not-allowed.description",
        );
      case "BlocksAssignmentNotAllowed":
        return i18n.t(
          "assignation-edit.incidence-table.validations.blocks-assignment-not-allowed.description",
        );
      case "ClassroomsAssignmentNotAllowed":
        return i18n.t(
          "assignation-edit.incidence-table.validations.classrooms-assignment-not-allowed.description",
        );
      case "ExternalGroupValidation":
        return i18n.t(
          "assignation-edit.incidence-table.validations.external-group-validation.description",
        );
      case "InvalidClassroomsCapacityAssignment":
        return i18n.t(
          "assignation-edit.incidence-table.validations.invalid-classrooms-capacity-assignment.description",
        );
      case "InvalidInstructorTravel":
        return i18n.t(
          "assignation-edit.incidence-table.validations.invalid-instructor-travel.description",
          {
            fromBuildingName: this.incidence?.withFollowing?.fromBuilding?.name,
            fromBuildingCode: this.incidence?.withFollowing?.fromBuilding?.code,
            toBuildingName: this.incidence?.withFollowing?.toBuilding?.name,
            toBuildingCode: this.incidence?.withFollowing?.toBuilding?.code,
          },
        );
      case "InvalidPastSessionEdition":
        return i18n.t(
          "assignation-edit.incidence-table.validations.invalid-past-session-edition.description",
        );
      case "InstructorAssignmentRequired":
        return i18n.t(
          "assignation-edit.incidence-table.validations.instructor-assignment-required.description",
        );
      case "InvalidSessionInHoliday":
        const holidayName = (this?.incidence?.holiday as unknown as Holiday)?.name;
        return this?.incidence?.allowed
          ? holidayName
          : ` ${i18n.t(
              "assignation-edit.incidence-table.validations.invalid-session-in-holiday.description",
              {
                holidayName,
              },
            )}`;
      case "InvalidExpectedBlocksByLink":
        return i18n.t(
          "assignation-edit.incidence-table.validations.invalid-expected-blocks-by-link.description",
          {
            programmedBlocks: this.incidence.programmedBlocks,
            weeklyBlocks: this.incidence.weeklyBlocks,
            numberOfWeeks: this.incidence.numberOfWeeks,
            totalBlocks: this.incidence.totalBlocks,
            programmedBlocksHours: this.incidence.programmedBlocks / 2,
            totalBlocksHours: this.incidence.totalBlocks / 2,
          },
        );

      default:
        return errors[this.incidence.__typename];
    }
  }
}

export default SessionByType;
